import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HeroUIProvider } from "@heroui/react";
import * as Sentry from "@sentry/react";
import { installDomRemovalDiagnostics } from "./services/dom-removal-diagnostics";
import { Router } from "./router";
import "./index.css";
import "./i18n";

// Init only when SENTRY_DSN is present in the build env. Local dev silent
// unless explicitly opted in. The vite plugin (already in vite.config.ts)
// uploads source maps regardless; Sentry.init here is what actually captures
// runtime events.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Strip "-v2" suffix so Sentry environments read "production" / "preview"
    // not "production-v2". Railway must set VITE_SENTRY_ENVIRONMENT per env;
    // vite bakes env vars at build time so this resolves at compile, not run.
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT?.replace(/-v2$/, "") ??
      import.meta.env.MODE,
    // Vite maps Railway's build SHA into this field. Other build environments
    // can provide VITE_RAILWAY_GIT_COMMIT_SHA explicitly.
    release: import.meta.env.VITE_RAILWAY_GIT_COMMIT_SHA,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing — 10% in prod per Sentry's low-traffic guidance (<100K MAU).
    // Customer-web is our highest-traffic surface but still well under that
    // ceiling. 10% is the documented sweet spot for the Team plan's quota
    // envelope; raise toward 0.05 only if we cross 200K Performance Units/mo.
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    tracePropagationTargets: [
      /^https:\/\/ezx-server-prod\.trypreview\.app\/api/,
      /^https:\/\/ezx-server-preview\.trypreview\.app\/api/,
      "localhost",
    ],
    // Session Replay — 10% session sample per Sentry's docs for medium-traffic
    // surfaces (10K-100K sessions/day). Error sessions always 100% so the
    // useful ones (the ones with bugs) are never missed.
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

const root = document.getElementById("root");
if (!root) throw new Error("Application root is missing");
const domDiagnostics = import.meta.env.VITE_SENTRY_DSN
  ? installDomRemovalDiagnostics(root)
  : undefined;
import.meta.hot?.dispose(() => domDiagnostics?.dispose());

createRoot(root, {
  onUncaughtError: domDiagnostics?.onUncaughtError,
}).render(
  <StrictMode>
    <HeroUIProvider>
      <Router />
    </HeroUIProvider>
  </StrictMode>
);
